import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Library, Target, CalendarCheck, Flame, ArrowRight, Check } from 'lucide-react';
import { Card } from '../../components/UI';
import { statsApi, checkinsApi, goalsApi } from '../../api';
import './Dashboard.css';

const Dashboard = () => {
  const [stats, setStats] = useState({
    materials: 0,
    activeGoals: 0,
    completedGoals: 0,
    totalCheckins: 0
  });
  const [streak, setStreak] = useState({ streak: 0, total: 0 });
  const [todayCheckin, setTodayCheckin] = useState(null);
  const [recentGoals, setRecentGoals] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const [statsRes, streakRes, todayRes, goalsRes] = await Promise.all([
        statsApi.get(),
        checkinsApi.getStreak(),
        checkinsApi.getToday(),
        goalsApi.getAll()
      ]);
      
      setStats(statsRes.data);
      setStreak(streakRes.data);
      setTodayCheckin(todayRes.data.checkin);
      setRecentGoals(goalsRes.data.slice(0, 3));
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleQuickCheckin = async () => {
    try {
      await checkinsApi.create({});
      fetchDashboardData();
    } catch (error) {
      console.error('Error checking in:', error);
    }
  };

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good Morning';
    if (hour < 18) return 'Good Afternoon';
    return 'Good Evening';
  };

  const getCurrentDate = () => {
    return new Date().toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="loading-state">
        <div className="loading-spinner" />
        <p>Loading dashboard...</p>
      </div>
    );
  }

  return (
    <div className="dashboard-page">
      {/* Welcome Section */}
      <div className="welcome-section">
        <div className="welcome-text">
          <h1>{getGreeting()}, Samuel</h1>
          <p className="welcome-date">{getCurrentDate()}</p>
        </div>
        
        {!todayCheckin && (
          <button className="quick-checkin-btn" onClick={handleQuickCheckin}>
            <CalendarCheck size={20} />
            <span>Quick Check-in</span>
          </button>
        )}
      </div>

      {/* Stats Grid */}
      <div className="stats-grid">
        <Card className="stat-card" hover>
          <Link to="/materials" className="stat-link">
            <div className="stat-icon materials">
              <Library size={24} />
            </div>
            <div className="stat-info">
              <div className="stat-value">{stats.materials}</div>
              <div className="stat-label">Materials Saved</div>
            </div>
          </Link>
        </Card>

        <Card className="stat-card" hover>
          <Link to="/goals" className="stat-link">
            <div className="stat-icon goals">
              <Target size={24} />
            </div>
            <div className="stat-info">
              <div className="stat-value">{stats.activeGoals}</div>
              <div className="stat-label">Active Goals</div>
            </div>
          </Link>
        </Card>

        <Card className="stat-card" hover>
          <Link to="/checkin" className="stat-link">
            <div className="stat-icon checkins">
              <CalendarCheck size={24} />
            </div>
            <div className="stat-info">
              <div className="stat-value">{stats.totalCheckins}</div>
              <div className="stat-label">Total Check-ins</div>
            </div>
          </Link>
        </Card>

        <Card className="stat-card streak-card" hover>
          <Link to="/checkin" className="stat-link">
            <div className="stat-icon streak">
              <Flame size={24} />
            </div>
            <div className="stat-info">
              <div className="stat-value">{streak.streak}</div>
              <div className="stat-label">Day Streak</div>
            </div>
          </Link>
        </Card>
      </div>

      {/* Today's Status */}
      <Card className="today-status-card" variant="gold">
        <div className="today-header">
          <h2>Today's Status</h2>
          {todayCheckin && (
            <div className="checked-badge">
              <Check size={16} />
              <span>Checked In</span>
            </div>
          )}
        </div>
        
        <div className="today-content">
          {todayCheckin ? (
            <div className="today-message">
              <p>You've checked in today. Keep up the great work!</p>
              {todayCheckin.note && (
                <blockquote className="today-note">"{todayCheckin.note}"</blockquote>
              )}
            </div>
          ) : (
            <div className="today-message">
              <p>You haven't checked in yet today.</p>
              <Link to="/checkin" className="checkin-link">
                Go to Check-in <ArrowRight size={16} />
              </Link>
            </div>
          )}
        </div>
      </Card>

      {/* Recent Goals */}
      {recentGoals.length > 0 && (
        <div className="recent-section">
          <div className="section-header">
            <h2>Recent Goals</h2>
            <Link to="/goals" className="view-all-link">
              View All <ArrowRight size={16} />
            </Link>
          </div>
          
          <div className="recent-goals-list">
            {recentGoals.map(goal => (
              <Card key={goal.id} className="recent-goal-card">
                <div className="goal-info">
                  <h4>{goal.title}</h4>
                  <div className="goal-progress-mini">
                    <div className="progress-bar-mini">
                      <div 
                        className="progress-fill-mini" 
                        style={{ width: `${goal.progress}%` }}
                      />
                    </div>
                    <span className="progress-text">{goal.progress}%</span>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Motivational Quote */}
      <Card className="quote-card">
        <blockquote className="daily-quote">
          "The secret of getting ahead is getting started. The secret of getting started is breaking your complex overwhelming tasks into small manageable tasks, and then starting on the first one."
        </blockquote>
        <cite className="quote-author">— Mark Twain</cite>
      </Card>
    </div>
  );
};

export default Dashboard;
